import React, { useMemo, useState } from "react";

import { useAppContext } from "@app/helpers/hooks";
import { hasRefundProtectionPurchased } from "@app/modules/account/components/trip-details/helpers";
import type {
  NapiCancellationOption,
  NapiPurchaseCancellation
} from "@app/types/napi-v2-cancellation-types";
import type { NapiV2Purchase } from "@app/types/napi-v2-purchase-types";
import type { CreditType } from "@app/types/napi-v2-request-type";

import { CancelTripContext } from "./cancel-trip-context";
import { CancelTripConfirmation } from "./cancellation-confirmation";
import { CancellationProgress } from "./cancellation-progress";
import { CancellationStatusInfo } from "./cancellation-status-info";

export type RefundFormState =
  | "not_refundable_anymore"
  | "already_cancelled"
  | "success"
  | "pending"
  | "error"
  | "denied"
  | undefined;

interface StatusInfoProps {
  status: RefundFormState;
  purchase_cancellation?: NapiPurchaseCancellation;
}

interface ConfirmationProps {
  cancellation_options: NapiCancellationOption[];
  onConfirm: (refund_method?: CreditType) => void;
}

const MODALS = {
  status_info: ({ status, purchase_cancellation }: StatusInfoProps) => {
    return (
      <CancellationStatusInfo
        status={status}
        purchase_cancellation={purchase_cancellation}
      />
    );
  },
  confirmation: ({ cancellation_options, onConfirm }: ConfirmationProps) => {
    return (
      <CancelTripConfirmation
        cancellation_options={cancellation_options}
        onConfirm={onConfirm}
      />
    );
  }
};

const ModalTypes = {
  STATUS_INFO: "status_info",
  CONFIRMATION: "confirmation"
} satisfies Record<string, keyof typeof MODALS>;

interface Props {
  purchase: NapiV2Purchase;
  cancellation_options: NapiCancellationOption[];
  updatePurchaseState: (response: NapiPurchaseCancellation) => void;
}

export const CancelTrip: React.FC<Props> = ({
  purchase,
  cancellation_options,
  updatePurchaseState
}) => {
  const { api } = useAppContext();
  const [is_loading, setLoading] = useState(false);
  const [refund_cancellation_state, setCancellationState] =
    useState<RefundFormState>(undefined);
  const [refund_method, setRefundMethod] = useState<CreditType | undefined>(
    undefined
  );
  const [purchase_cancellation, setPurchaseCancellation] = useState<
    NapiPurchaseCancellation | undefined
  >(undefined);

  const { id, uuid, items } = purchase;

  const onCancelConfirmation = (
    refund_method: CreditType = "original_payment_method"
  ) => {
    setLoading(true);
    setRefundMethod(refund_method);

    api
      .cancelPurchase(id, cancellation_options, uuid, refund_method)
      .then(handleCancelResponse)
      .catch(() => {
        setCancellationState("error");
      })
      .finally(() => setLoading(false));
  };

  const handleCancelResponse = (response: NapiPurchaseCancellation) => {
    updatePurchaseState(response);
    switch (response.state) {
      case "success":
        setPurchaseCancellation(response);
        setCancellationState(response.state);
        break;
      case "pending":
        setCancellationState(response.state);
        break;
      default:
        // denied, failed
        setCancellationState("error");
    }
  };

  const modalType = useMemo(() => {
    switch (refund_cancellation_state) {
      case "success":
      case "pending":
      case "error":
        return ModalTypes.STATUS_INFO;
      default:
        return ModalTypes.CONFIRMATION;
    }
  }, [refund_cancellation_state]) satisfies keyof typeof MODALS;

  if (is_loading) {
    return <CancellationProgress />;
  }

  const Modal = MODALS[modalType];
  const all_props = {
    purchase_cancellation,
    status: refund_cancellation_state as "success" | "pending" | "error",
    cancellation_options,
    onConfirm: onCancelConfirmation
  };

  return (
    <CancelTripContext.Provider
      value={{
        purchase,
        refund_cancellation_state,
        selected_cancellation_option: refund_method,
        display_hepstar_banner: hasRefundProtectionPurchased(items)
      }}
    >
      <Modal {...all_props} />
    </CancelTripContext.Provider>
  );
};
